import { jsonToGraphQLQuery } from 'lib/core/resources/helpers/jsonToGraphql'

interface Options {
  withConfigurableVariants?: boolean
  withCrosssell?: boolean
  withUpsell?: boolean
  withRelated?: boolean
  withProductLinks?: boolean
  withMediaGallery?: boolean
  withCustomAttributes?: boolean
  withBreadcrumb?: boolean
  withReviewSummary?: boolean
  withOnConfigurable?: boolean
  withOnGiftCard?: boolean
  withOnCustomizable?: boolean
  withBase?: boolean
  withOnGroupedProduct?: boolean
  withStock?: boolean
}

export const getProductQuery = (keyName: string, options?: Options, castObject?: boolean): any => { // TODO: type
  const {
    withConfigurableVariants,
    withCrosssell,
    withUpsell,
    withRelated,
    withProductLinks,
    withMediaGallery,
    withCustomAttributes,
    withBreadcrumb,
    withReviewSummary,
    withOnConfigurable,
    withOnGiftCard,
    withOnCustomizable,
    withBase,
    withOnGroupedProduct,
    withStock
  } = options ?? {}

  const subProductConfig = {
    withBase: true,
    withMediaGallery: true,
    withCustomAttributes: true
  }

  let query: any = {
    id: true,
    name: true,
    sku: true,
    url_key: true,
    canonical_url: true,
    url_rewrites: {
      url: true
    }
  }

  if (withBase) {
    query = {
      ...query,

      short_description: {
        html: true
      },
      image: {
        label: true,
        url: true
      },
      small_image: {
        label: true,
        url: true
      },
      thumbnail: {
        label: true,
        url: true
      },
      description: {
        html: true
      },
      new_from_date: true,
      new_to_date: true,
      status: true,
      stock_status: true,
      meta_description: true,
      meta_keyword: true,
      meta_title: true,
      categories: {
        id: true,
        name: true
      },
      price: {
        regularPrice: {
          amount: {
            currency: true,
            value: true
          }
        },
        minimalPrice: {
          amount: {
            currency: true,
            value: true
          }
        },
        maximalPrice: {
          amount: {
            currency: true,
            value: true
          }
        }
      },
      price_range: {
        maximum_price: {
          discount: {
            amount_off: true,
            percent_off: true
          },
          final_price: {
            currency: true,
            value: true
          },
          fixed_product_taxes: {
            amount: {
              currency: true,
              value: true
            },
            label: true
          },
          regular_price: {
            currency: true,
            value: true
          }
        },
        minimum_price: {
          discount: {
            amount_off: true,
            percent_off: true
          },
          final_price: {
            currency: true,
            value: true
          },
          fixed_product_taxes: {
            amount: {
              currency: true,
              value: true
            },
            label: true
          },
          regular_price: {
            currency: true,
            value: true
          }
        }
      },
      pwa_layout: true,
      __typename: true
    }
  }

  query.__on = []

  if (withReviewSummary) {
    query.review_summary = {
      reviews_count: true,
      rating_summary: true
    }
  }

  if (withStock) {
    query.only_x_left_in_stock = true
  }

  if (withCrosssell) {
    const _subProductsQuery = getProductQuery('items', subProductConfig, true)
    query.crosssell_products = {
      ..._subProductsQuery.items
    }
  }

  if (withUpsell) {
    const _subProductsQuery = getProductQuery('items', subProductConfig, true)
    query.upsell_products = {
      ..._subProductsQuery.items
    }
  }

  if (withRelated) {
    const _subProductsQuery = getProductQuery('items', subProductConfig, true)
    query.related_products = {
      ..._subProductsQuery.items
    }
  }

  if (withBreadcrumb) {
    query.categories = {
      id: true,
      name: true,
      breadcrumbs: {
        category_url_key: true,
        category_name: true
      }
    }
  }

  if (withCustomAttributes) {
    query.visiture_custom_attributes = true
  }

  if (withProductLinks) { // TODO: deprecate?
    query.product_links = {
      sku: true,
      link_type: true,
      linked_product_sku: true,
      linked_product_type: true,
      position: true
    }
  }

  if (withMediaGallery) {
    query.media_gallery_entries = {
      id: true,
      media_type: true,
      label: true,
      position: true,
      disabled: true,
      file: true,
      video_content: {
        media_type: true,
        video_provider: true,
        video_url: true,
        video_title: true,
        video_description: true,
        video_metadata: true
      }
    }
  }

  if (withOnGiftCard) {
    query.__on.push({
      __typeName: 'GiftCardProduct',
      allow_message: true,
      message_max_length: true,
      allow_open_amount: true,
      open_amount_min: true,
      open_amount_max: true,
      is_returnable: true,
      is_redeemable: true,
      giftcard_type: true,
      giftcard_amounts: {
        value_id: true,
        website_id: true,
        value: true,
        attribute_id: true,
        website_value: true
      }
    })
  }

  if (withOnGroupedProduct) {
    query.__on.push({
      __typeName: 'GroupedProduct',
      items: {
        qty: true,
        position: true,
        product: {
          sku: true,
          name: true,
          __typename: true,
          url_key: true,
          price: {
            regularPrice: {
              amount: {
                currency: true,
                value: true
              }
            }
          }
        }
      }
    })
  }

  if (withOnConfigurable) {
    const _subProductsQuery = getProductQuery('items', {
      ...subProductConfig,
      withReviewSummary: false,
      withStock: true
    }, true)

    const isDefaultConfigurable = !process.env.REACT_APP_DISABLE_DEFAULT_CONFIGURABLE
    const configurableVariant = !withConfigurableVariants ? false : {
      product: {
        is_default_selection: isDefaultConfigurable,
        ..._subProductsQuery.items
      },
      attributes: {
        label: true,
        code: true,
        value_index: true
      }
    }

    query.__on.push({
      __typeName: 'ConfigurableProduct',
      configurable_options: {
        attribute_code: true,
        attribute_id: true,
        id: true,
        label: true,
        position: true,
        use_default: true,
        product_id: true,
        values: {
          default_label: true,
          label: true,
          store_label: true,
          use_default_value: true,
          value_index: true,
          visiture_swatch_data: {
            type: true,
            value: true
          }
        }
      },
      variants: configurableVariant
    })
  }

  if (withOnCustomizable) {
    query.__on.push({
      __typeName: 'CustomizableProductInterface',
      options: {
        title: true,
        required: true,
        sort_order: true,
        option_id: true,
        __on: [
          {
            __typeName: 'CustomizableFieldOption',
            field_value: {
              __aliasFor: 'value',
              max_characters: true,
              price: true,
              price_type: true,
              sku: true
            }
          },
          {
            __typeName: 'CustomizableMultipleOption',
            multiple_value: {
              __aliasFor: 'value',
              option_type_id: true,
              price_type: true,
              price: true,
              sku: true,
              sort_order: true,
              title: true
            }
          },
          {
            __typeName: 'CustomizableAreaOption',
            area_value: {
              __aliasFor: 'value',
              max_characters: true,
              price: true,
              price_type: true,
              sku: true
            }
          },
          {
            __typeName: 'CustomizableDropDownOption',
            dropdown_value: {
              __aliasFor: 'value',
              option_type_id: true,
              price: true,
              price_type: true,
              sku: true,
              sort_order: true,
              title: true
            }
          },
          {
            __typeName: 'CustomizableCheckboxOption',
            checkbox_value: {
              __aliasFor: 'value',
              option_type_id: true,
              price: true,
              price_type: true,
              sku: true,
              sort_order: true,
              title: true
            }
          },
          {
            __typeName: 'CustomizableRadioOption',
            radio_value: {
              __aliasFor: 'value',
              title: true,
              option_type_id: true,
              price: true,
              price_type: true,
              sku: true,
              sort_order: true
            }
          }
        ]
      }
    })
  }

  if (Array.isArray(query.__on) && !query.__on.length) {
    delete query.__on
  }

  const productsQuery = {
    [keyName]: query
  }

  return castObject ? productsQuery : jsonToGraphQLQuery(productsQuery)
}
